import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Mail, Phone, MessageSquare, Linkedin, Twitter, ChevronRight, Loader2, AlertCircle } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export const FinalCTASection = () => {
  return (
    <section className="py-24 cta-gradient" data-testid="final-cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
          <motion.h2 variants={fadeUp} className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Récupérez 2h42 de cours par semaine
          </motion.h2>
          <motion.p variants={fadeUp} className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Rejoignez les établissements qui ont transformé leur gestion de classe avec EduPlan.
          </motion.p>
          <motion.div variants={fadeUp}>
            <a href="#contact" className="inline-flex items-center px-10 py-5 bg-white text-[#E7A541] rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300" data-testid="final-cta-btn">
              Demander une démonstration
              <ArrowRight className="ml-2" size={22} />
            </a>
          </motion.div>
          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-8 mt-10 text-white/80">
            <span className="flex items-center gap-2"><Check size={18} /> Déploiement en 3-7 jours</span>
            <span className="flex items-center gap-2"><Check size={18} /> Formation incluse</span>
            <span className="flex items-center gap-2"><Check size={18} /> Support illimité</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", establishment: "", role: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Appel direct à Airtable
      const AIRTABLE_API_KEY = "patqCuiYcTfLKD1Er.36e71dd47caa29e742a646bf197f5c61a2b24cd84b8f70e2c5e278e282eca6c5";
      const AIRTABLE_BASE_ID = "appRQ4Y5o8vZ0gwJe";
      const AIRTABLE_TABLE_ID = "tblnt4Hy1KiYWPjqQ";
      
      const response = await fetch(`https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${AIRTABLE_TABLE_ID}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${AIRTABLE_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fields: {
            "Name": formData.name,
            "Email": formData.email,
            "Role": formData.role,
            "Establishment": formData.establishment,
            "Message": formData.message || "",
            "Source": "Landing Page",
            "Submitted At": new Date().toISOString().split('T')[0]
          }
        }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        console.error("Airtable error:", data);
        setError("Une erreur est survenue. Veuillez réessayer.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#F9F9FA]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.h2 variants={fadeUp} className="font-display text-3xl md:text-4xl font-bold text-[#29282B] mb-6">
              Demandez une démonstration
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#29282B]/70 text-lg mb-10">
              Notre équipe vous contacte sous 24h pour organiser une présentation personnalisée d'EduPlan.
            </motion.p>
            <motion.div variants={fadeUp} className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FDF6E9] flex items-center justify-center">
                  <Mail className="text-[#E7A541]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-[#29282B]/60">Email</p>
                  <a href="mailto:contact@eduplan-lnc.com" className="font-medium text-[#29282B] hover:text-[#E7A541]">
                    contact@eduplan-lnc.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FDF6E9] flex items-center justify-center">
                  <MessageSquare className="text-[#E7A541]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-[#29282B]/60">Réponse</p>
                  <p className="font-medium text-[#29282B]">Sous 24h ouvrées</p>
                </div>
              </div>
            </motion.div>

            {/* Trust badges */}
            <motion.div variants={fadeUp} className="mt-10 pt-10 border-t border-[#D9DADC]">
              <p className="text-sm text-[#29282B]/60 mb-4">Ils nous font confiance :</p>
              <div className="flex flex-wrap gap-4">
                <span className="px-4 py-2 bg-white rounded-full text-sm text-[#29282B]/70 border border-[#D9DADC]">Collèges</span>
                <span className="px-4 py-2 bg-white rounded-full text-sm text-[#29282B]/70 border border-[#D9DADC]">Lycées</span>
                <span className="px-4 py-2 bg-white rounded-full text-sm text-[#29282B]/70 border border-[#D9DADC]">Académies</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            {submitted ? (
              <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <Check className="text-green-600" size={32} />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#29282B] mb-4">
                  Demande envoyée !
                </h3>
                <p className="text-[#29282B]/70">
                  Nous vous recontactons sous 24h pour organiser votre démonstration.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8" data-testid="contact-form">
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                    <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#29282B] mb-2">Nom complet *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 border border-[#D9DADC] rounded-lg input-focus focus:outline-none"
                        placeholder="Jean Dupont"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#29282B] mb-2">Fonction *</label>
                      <select
                        required
                        value={formData.role}
                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                        className="w-full px-4 py-3 border border-[#D9DADC] rounded-lg input-focus focus:outline-none bg-white"
                        data-testid="contact-role-input"
                      >
                        <option value="">Sélectionner...</option>
                        <option value="Chef d'établissement">Chef d'établissement</option>
                        <option value="CPE">CPE</option>
                        <option value="Professeur">Professeur</option>
                        <option value="Vie scolaire">Vie scolaire</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#29282B] mb-2">Email professionnel *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 border border-[#D9DADC] rounded-lg input-focus focus:outline-none"
                      placeholder="jean.dupont@college.fr"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#29282B] mb-2">Établissement *</label>
                    <input
                      type="text"
                      required
                      value={formData.establishment}
                      onChange={(e) => setFormData({...formData, establishment: e.target.value})}
                      className="w-full px-4 py-3 border border-[#D9DADC] rounded-lg input-focus focus:outline-none"
                      placeholder="Collège Jean Moulin, Lyon"
                      data-testid="contact-establishment-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#29282B] mb-2">Message (optionnel)</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-3 border border-[#D9DADC] rounded-lg input-focus focus:outline-none resize-none"
                      placeholder="Précisez vos besoins ou questions..."
                      data-testid="contact-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-[#E7A541] text-white rounded-lg font-semibold hover:bg-[#D4913A] transition-all duration-300 btn-shine disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={20} />
                        Envoi en cours...
                      </>
                    ) : (
                      "Demander ma démonstration"
                    )}
                  </button>
                  <p className="text-xs text-[#29282B]/50 text-center">
                    En soumettant ce formulaire, vous acceptez notre{" "}
                    <a href="https://rgpd.eduplan-lnc.com/" target="_blank" rel="noopener noreferrer" className="text-[#E7A541] hover:underline">
                      politique de confidentialité
                    </a>
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#29282B] hex-pattern-dark pt-20 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 pb-12 border-b border-white/10">
          <div className="col-span-2 md:col-span-1">
            <img src="/logo-eduplan-white.png" alt="EduPlan" className="h-12 w-auto mb-4" />
            <p className="text-white/60 text-sm">Une École Un Plan</p>
            <p className="text-white/40 text-xs mt-2">+2h42 de cours par semaine</p>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-4">Produit</h4>
            <ul className="space-y-3">
              <li><a href="#features" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">Fonctionnalités</a></li>
              <li><a href="#pricing" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">Tarifs</a></li>
              <li><a href="#faq" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">FAQ</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-4">Ressources</h4>
            <ul className="space-y-3">
              <li><a href="#contact" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">Centre d'aide</a></li>
              <li><a href="#contact" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">Documentation</a></li>
              <li><a href="#contact" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">Support</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-4">Légal</h4>
            <ul className="space-y-3">
              <li><a href="https://rgpd.eduplan-lnc.com/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">Mentions légales</a></li>
              <li><a href="https://rgpd.eduplan-lnc.com/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">CGV</a></li>
              <li><a href="https://rgpd.eduplan-lnc.com/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">CGU</a></li>
              <li><a href="https://rgpd.eduplan-lnc.com/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">RGPD & Confidentialité</a></li>
              <li><a href="https://rgpd.eduplan-lnc.com/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">Cookies</a></li>
              <li><a href="https://rgpd.eduplan-lnc.com/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link">Stockage des données</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:contact@eduplan-lnc.com" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link flex items-center gap-2">
                  <Mail size={14} /> contact@eduplan-lnc.com
                </a>
              </li>
              <li>
                <a href="https://www.linkedin.com/company/groupe-lnc/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#E7A541] transition-colors text-sm footer-link flex items-center gap-2">
                  <Linkedin size={14} /> LinkedIn
                </a>
              </li>
            </ul>
            <div className="mt-6 pt-4 border-t border-white/10">
              <a href="https://eduplan-lnc.com/auth/login" className="inline-flex items-center text-[#E7A541] font-medium text-sm hover:underline" data-testid="footer-portal-link">
                Portail de connexion
                <ChevronRight size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between pt-8 gap-4">
          <p className="text-white/50 text-sm">© {currentYear} EduPlan - Fait avec ❤️ en France</p>
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <span>🇫🇷</span>
            <span>Données hébergées en Europe</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FinalCTASection;
